import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, CreditCard, AlertTriangle, Upload, Image, RefreshCw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Button from '../components/UI/Button';
import Input from '../components/UI/Input';
import Select from '../components/UI/Select';
import Card from '../components/UI/Card';
import { STRIPE_PRODUCTS } from '../stripe-config';
import { createCheckoutSession, cancelSubscription } from '../lib/stripe';

const CURRENCIES = [
  { value: 'EUR', label: 'Euro (€)', symbol: '€' },
  { value: 'USD', label: 'US Dollar ($)', symbol: '$' },
  { value: 'GBP', label: 'British Pound (£)', symbol: '£' },
  { value: 'CHF', label: 'Swiss Franc (CHF)', symbol: 'CHF' },
  { value: 'CAD', label: 'Canadian Dollar (C$)', symbol: 'C$' },
  { value: 'AUD', label: 'Australian Dollar (A$)', symbol: 'A$' }
];

const AccountSettings: React.FC = () => {
  const { user, isLoading, hasActiveSubscription, updateProfile, updateUserLogo, refreshSubscription } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(user?.name || '');
  const [company, setCompany] = useState(user?.company || '');
  const [siret, setSiret] = useState(user?.siret || '');
  const [vatNumber, setVatNumber] = useState(user?.vat_number || '');
  const [address, setAddress] = useState(user?.address || '');
  const [city, setCity] = useState(user?.city || '');
  const [postalCode, setPostalCode] = useState(user?.postal_code || '');
  const [country, setCountry] = useState(user?.country || 'France');
  const [invoicePrefix, setInvoicePrefix] = useState(user?.invoice_prefix || 'INV-');
  const [quotePrefix, setQuotePrefix] = useState(user?.quote_prefix || 'QUO-');
  const [nextInvoiceNumber, setNextInvoiceNumber] = useState(user?.next_invoice_number?.toString() || '001');
  const [nextQuoteNumber, setNextQuoteNumber] = useState(user?.next_quote_number?.toString() || '001');
  const [currency, setCurrency] = useState(user?.currency || 'EUR');
  const [isAnnual, setIsAnnual] = useState(true);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const handleRefreshSubscription = async () => {
    setIsRefreshing(true);
    try {
      await refreshSubscription();
      setSuccessMessage('Abonnement actualisé avec succès');
    } catch (error) {
      console.error('Error refreshing subscription:', error);
      setError('Erreur lors de l\'actualisation de l\'abonnement');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!user) {
      setError('Vous devez être connecté pour mettre à jour votre logo');
      return;
    }
    if (file) {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64String = reader.result as string;
          await updateUserLogo(base64String);
          setSuccessMessage('Logo mis à jour avec succès');
        } catch (err) {
          setError('Erreur lors de la mise à jour du logo');
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setIsSaving(true);

    try {
      await updateProfile({
        name,
        company,
        siret,
        vat_number: vatNumber,
        address,
        postal_code: postalCode,
        city,
        country,
        invoice_prefix: invoicePrefix,
        quote_prefix: quotePrefix,
        next_invoice_number: parseInt(nextInvoiceNumber),
        next_quote_number: parseInt(nextQuoteNumber),
        currency,
        currency_symbol: CURRENCIES.find(c => c.value === currency)?.symbol || '€',
      });
      setSuccessMessage('Profil mis à jour avec succès');
    } catch (err) {
      setError('Erreur lors de la mise à jour du profil');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubscribe = async (plan: 'ESSENTIAL' | 'PREMIUM') => {
    try {
      const productKey = `${plan}${isAnnual ? '_ANNUAL' : ''}` as keyof typeof STRIPE_PRODUCTS;
      const url = await createCheckoutSession(
        plan,
        STRIPE_PRODUCTS[productKey].priceId,
        `${window.location.origin}/app/account`,
        `${window.location.origin}/`
      );
      
      if (!url) {
        throw new Error('Failed to create checkout session');
      }
      
      window.location.href = url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      setError('Erreur lors de la mise à jour de l\'abonnement');
    }
  };

  // Determine current plan based on subscription tier and period
  const currentPlan = user?.subscription_tier;
  const currentPeriod = user?.subscription_period;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
      <div className="flex items-center mb-8">
        <Button 
          variant="ghost" 
          className="mr-4"
          onClick={() => navigate('/app')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Paramètres du compte</h1>
          {!company || !address || !postalCode || !city || !siret && (
            <p className="text-error-500 mt-2">
              Veuillez remplir tous les champs obligatoires pour accéder à toutes les fonctionnalités
            </p>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-error-50 p-4 text-error-500 dark:bg-error-900/20">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="rounded-md bg-green-50 p-4 text-green-600 dark:bg-green-900/20 dark:text-green-400">
          {successMessage}
        </div>
      )}

      <div className="grid gap-8">
        <Card>
          <Card.Header title="Informations de l'entreprise" />
          <form onSubmit={handleProfileUpdate}>
            <Card.Content className="space-y-6">
              <div className="flex justify-center mb-6">
                <div className="text-center">
                  {user?.logo ? (
                    <img
                      src={user.logo}
                      alt="Logo de l'entreprise"
                      className="h-24 w-auto mx-auto mb-4"
                    />
                  ) : (
                    <div className="h-24 w-24 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-4">
                      <Upload className="h-12 w-12 text-gray-400" />
                    </div>
                  )}
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    accept="image/*"
                    onChange={handleLogoUpload}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    leftIcon={user?.logo ? <Image className="h-4 w-4" /> : <Upload className="h-4 w-4" />}
                  >
                    {user?.logo ? 'Changer le logo' : 'Ajouter un logo'}
                  </Button>
                </div>
              </div>

              <Input
                label="Nom"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />

              <Input
                label="Entreprise"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                required
              />

              <Input
                label="Numéro SIRET"
                value={siret}
                onChange={(e) => setSiret(e.target.value)}
                required
              />

              <Input
                label="Numéro de TVA"
                value={vatNumber}
                onChange={(e) => setVatNumber(e.target.value)}
                placeholder="FR12345678900"
              />

              <Input
                label="Adresse"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                required
              />

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Code postal"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  required
                />

                <Input
                  label="Ville"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  required
                />
              </div>

              <Input
                label="Pays"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                required
              />

              <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
                <h3 className="text-lg font-medium mb-4">Format de numérotation</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-700 dark:text-gray-300">Factures</h4>
                    <Input
                      label="Préfixe des factures"
                      value={invoicePrefix}
                      onChange={(e) => setInvoicePrefix(e.target.value)}
                      placeholder="Ex: FAC-"
                    />
                    <Input
                      label="Prochain numéro de facture"
                      type="number"
                      value={nextInvoiceNumber}
                      onChange={(e) => setNextInvoiceNumber(e.target.value)}
                      min="1"
                    />
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-700 dark:text-gray-300">Devis</h4>
                    <Input
                      label="Préfixe des devis"
                      value={quotePrefix}
                      onChange={(e) => setQuotePrefix(e.target.value)}
                      placeholder="Ex: DEV-"
                    />
                    <Input
                      label="Prochain numéro de devis"
                      type="number"
                      value={nextQuoteNumber}
                      onChange={(e) => setNextQuoteNumber(e.target.value)}
                      min="1"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
                <h3 className="text-lg font-medium mb-4">Devise</h3>
                <Select
                  label="Devise par défaut"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  options={CURRENCIES}
                />
              </div>
            </Card.Content>
            <Card.Footer>
              <Button
                type="submit"
                isLoading={isSaving}
                leftIcon={<Save className="h-4 w-4" />}
              >
                Enregistrer les modifications
              </Button>
            </Card.Footer>
          </form>
        </Card>

        <Card>
          <Card.Header 
            title="Abonnement" 
            subtitle={
              currentPlan ? (
                `Vous êtes actuellement sur le plan ${currentPlan === 'PREMIUM' ? 'Premium' : 'Essentiel'} ${currentPeriod || ''}`
              ) : (
                'Aucun abonnement actif'
              )
            }
            action={
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefreshSubscription}
                isLoading={isRefreshing}
                leftIcon={<RefreshCw className="h-4 w-4" />}
              >
                Actualiser
              </Button>
            }
          />
          <Card.Content>
            {showCancelConfirm ? (
              <div className="bg-error-50 dark:bg-error-900/20 p-6 rounded-lg border border-error-200 dark:border-error-700 mb-6">
                <div className="flex items-start">
                  <AlertTriangle className="h-6 w-6 text-error-500 mr-4 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-semibold text-error-700 dark:text-error-300">
                      Confirmer l'annulation
                    </h3>
                    <p className="mt-2 text-error-600 dark:text-error-400">
                      Êtes-vous sûr de vouloir annuler votre abonnement ? Votre accès sera maintenu jusqu'à la fin de la période de facturation en cours.
                    </p>
                    <div className="mt-4 flex gap-4">
                      <Button
                        variant="outline"
                        onClick={() => setShowCancelConfirm(false)}
                      >
                        Retour
                      </Button>
                      <Button
                        variant="outline"
                        className="text-error-500 border-error-300 hover:bg-error-50"
                        onClick={handleCancelSubscription}
                        isLoading={isLoading}
                      >
                        Confirmer l'annulation
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-center gap-4 mb-8">
                  <span className={`text-sm ${!isAnnual ? 'text-gray-900 dark:text-white font-semibold' : 'text-gray-500 dark:text-gray-400'}`}>
                    Mensuel
                  </span>
                  <button
                    onClick={() => setIsAnnual(!isAnnual)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                      isAnnual ? 'bg-primary-500' : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        isAnnual ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <span className={`text-sm ${isAnnual ? 'text-gray-900 dark:text-white font-semibold' : 'text-gray-500 dark:text-gray-400'}`}>
                    Annuel <span className="text-primary-500">(2 mois offerts)</span>
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className={`p-6 rounded-lg border ${currentPlan === 'ESSENTIAL' ? 'border-primary-500' : 'border-gray-200 dark:border-gray-700'}`}>
                    <h3 className="text-xl font-semibold mb-2">Plan Essentiel</h3>
                    <div className="text-3xl font-bold mb-4">
                      {isAnnual ? '31' : '37'}€ <span className="text-sm text-gray-500">/mois</span>
                    </div>
                    {isAnnual && (
                      <p className="text-sm text-primary-600 dark:text-primary-400 mb-4">
                        Économisez 74€ par an
                      </p>
                    )}
                    {currentPlan === 'ESSENTIAL' && currentPeriod === (isAnnual ? 'annual' : 'monthly') ? (
                      <Button variant="outline" disabled fullWidth>
                        Plan actuel
                      </Button>
                    ) : (
                      <Button 
                        variant="outline" 
                        fullWidth
                        onClick={() => handleSubscribe('ESSENTIAL')}
                        leftIcon={<CreditCard className="h-4 w-4" />}
                      >
                        {currentPlan === 'ESSENTIAL' ? 'Changer de périodicité' : 'Passer à l\'Essentiel'}
                      </Button>
                    )}
                  </div>

                  <div className={`p-6 rounded-lg border ${currentPlan === 'PREMIUM' ? 'border-primary-500' : 'border-gray-200 dark:border-gray-700'}`}>
                    <h3 className="text-xl font-semibold mb-2">Plan Premium</h3>
                    <div className="text-3xl font-bold mb-4">
                      {isAnnual ? '39' : '47'}€ <span className="text-sm text-gray-500">/mois</span>
                    </div>
                    {isAnnual && (
                      <p className="text-sm text-primary-600 dark:text-primary-400 mb-4">
                        Économisez 96€ par an
                      </p>
                    )}
                    {currentPlan === 'PREMIUM' && currentPeriod === (isAnnual ? 'annual' : 'monthly') ? (
                      <Button variant="outline" disabled fullWidth>
                        Plan actuel
                      </Button>
                    ) : (
                      <Button 
                        variant="primary" 
                        fullWidth
                        onClick={() => handleSubscribe('PREMIUM')}
                        leftIcon={<CreditCard className="h-4 w-4" />}
                      >
                        {currentPlan === 'PREMIUM' ? 'Changer de périodicité' : 'Passer au Premium'}
                      </Button>
                    )}
                  </div>
                </div>

                {currentPlan && (
                  <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <Button
                      variant="outline"
                      className="text-error-500 border-error-300 hover:bg-error-50"
                      onClick={() => setShowCancelConfirm(true)}
                    >
                      Annuler l'abonnement
                    </Button>
                  </div>
                )}
              </>
            )}
          </Card.Content>
        </Card>
      </div>
    </div>
  );
};

export default AccountSettings;